import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '@/services/api';
import { Plus, Edit, Trash2, Eye, EyeOff, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import '@/styles/AdminManagement.css';

const AdminProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    title: '', category: '', description: '', duration: '',
    technologies: [], image: '', link: '', published: true,
    metrics: [{ icon: 'Download', label: 'Downloads', value: '' }]
  });
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (!adminApi.isAuthenticated()) { navigate('/admin/login'); return; }
    loadProjects();
  }, [navigate]);

  const loadProjects = async () => {
    try {
      const data = await adminApi.getAllProjects();
      setProjects(data);
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const openForm = (project = null) => {
    if (project) {
      setEditingProject(project);
      setFormData({
        title: project.title,
        category: project.category,
        description: project.description,
        duration: project.duration,
        technologies: project.technologies || [],
        image: project.image,
        link: project.link,
        published: project.published,
        metrics: project.metrics || []
      });
    } else {
      setEditingProject(null);
      setFormData({
        title: '', category: '', description: '', duration: '',
        technologies: [], image: '', link: '', published: true,
        metrics: [{ icon: 'Download', label: 'Downloads', value: '' }]
      });
    }
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const projectData = {
        ...formData,
        technologies: formData.technologies.filter(t => t.trim() !== ''),
        order: editingProject ? editingProject.order : projects.length + 1
      };

      if (editingProject) {
        await adminApi.updateProject(editingProject.id, projectData);
        setProjects(projects.map(p => p.id === editingProject.id ? { ...editingProject, ...projectData } : p));
        toast({ title: "Project updated!" });
      } else {
        const newProject = await adminApi.createProject(projectData);
        setProjects([...projects, newProject]);
        toast({ title: "Project created!" });
      }
      setShowForm(false);
    } catch (error) {
      toast({ title: "Failed to save project", variant: "destructive" });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this project?')) return;
    try {
      await adminApi.deleteProject(id);
      setProjects(projects.filter(p => p.id !== id));
      toast({ title: "Project deleted" });
    } catch (error) {
      toast({ title: "Failed to delete", variant: "destructive" });
    }
  };

  const togglePublish = async (project) => {
    try {
      const updated = { ...project, published: !project.published };
      await adminApi.updateProject(project.id, updated);
      setProjects(projects.map(p => p.id === project.id ? updated : p));
      toast({ title: project.published ? "Unpublished" : "Published" });
    } catch (error) {
      toast({ title: "Failed to update", variant: "destructive" });
    }
  };

  if (loading) return <div className="admin-loading">Loading...</div>;

  return (
    <div className="admin-dashboard">
      <aside className="admin-sidebar">
        <div className="sidebar-header"><h2>AK Admin</h2></div>
        <nav className="sidebar-nav">
          <button className="nav-item" onClick={() => navigate('/admin/dashboard')}>Dashboard</button>
          <button className="nav-item" onClick={() => navigate('/admin/contacts')}>Messages</button>
          <button className="nav-item active">Projects</button>
          <button className="nav-item" onClick={() => navigate('/admin/articles')}>Articles</button>
          <button className="nav-item" onClick={() => navigate('/admin/experience')}>Experience</button>
          <button className="nav-item" onClick={() => navigate('/admin/settings')}>Settings</button>
        </nav>
      </aside>

      <main className="admin-content">
        <div className="content-header">
          <h1>Manage Projects ({projects.length})</h1>
          <button className="add-btn" onClick={() => openForm()}>
            <Plus size={20} />Add Project
          </button>
        </div>

        <div className="items-grid">
          {projects.map((project) => (
            <div key={project.id} className="item-card">
              <img src={project.image} alt={project.title} className="item-image" />
              <div className="item-content">
                <div className="item-header">
                  <h3>{project.title}</h3>
                  <span className="item-category">{project.category}</span>
                </div>
                <p className="item-description">{project.description}</p>
                <div className="item-actions">
                  <button onClick={() => togglePublish(project)} className="action-btn">
                    {project.published ? <Eye size={16} /> : <EyeOff size={16} />}
                    {project.published ? 'Published' : 'Draft'}
                  </button>
                  <button onClick={() => openForm(project)} className="action-btn">
                    <Edit size={16} />Edit
                  </button>
                  <button onClick={() => handleDelete(project.id)} className="action-btn danger">
                    <Trash2 size={16} />Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {showForm && (
          <div className="modal-overlay" onClick={() => setShowForm(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingProject ? 'Edit Project' : 'Add Project'}</h2>
                <button onClick={() => setShowForm(false)} className="close-btn"><X size={24} /></button>
              </div>
              <form onSubmit={handleSubmit} className="modal-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Title</label>
                    <input required value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label>Category</label>
                    <input required value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea required rows="3" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Duration</label>
                    <input required value={formData.duration} onChange={(e) => setFormData({...formData, duration: e.target.value})} placeholder="Jan 2020 - Dec 2022" />
                  </div>
                  <div className="form-group">
                    <label>Image URL</label>
                    <input required value={formData.image} onChange={(e) => setFormData({...formData, image: e.target.value})} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Technologies (comma separated)</label>
                  <input value={formData.technologies.join(', ')} onChange={(e) => setFormData({...formData, technologies: e.target.value.split(',').map(t => t.trim())})} placeholder="Flutter, Firebase, REST APIs" />
                </div>
                <div className="form-group">
                  <label>Project Link (URL)</label>
                  <input value={formData.link} onChange={(e) => setFormData({...formData, link: e.target.value})} placeholder="https://play.google.com/store/apps/..." />
                </div>
                <button type="submit" className="submit-btn">{editingProject ? 'Update' : 'Create'} Project</button>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminProjects;
